import { useState } from "react";
import classNames from "classnames";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { buildActionButtonClassName } from "~/components/build/buttonClasses";
import Dropdown from "~/components/shared/Dropdown";
import Icon from "~/components/shared/Icon";
import { CancelButton } from "~/components/build/Header/CancelButton";
import RetryFailedJobsButton from "~/components/build/Header/RetryFailedJobsButton";

export function CollapsedBuildActions() {
  const { build, setBuildData } = useBuild();

  return (
    <>
      <CancelButton
        build={build}
        setBuildData={setBuildData}
        // Tidy this up when we've got a single build experience
        className={classNames(buildActionButtonClassName, "hover:shadow-xs !text-red-500")}
      />
      <BuildActions />
    </>
  );
}

/**
 * Build actions component
 * Renders either a single action as a button or a combo-button/dropdown depending on
 * various build attributes and user permissions
 * @returns JSX.Element
 */
function BuildActions() {
  const { build, setBuildData } = useBuild();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const waitingForStats = build.statistics === undefined;

  const showRetryFailures =
    build.permissions.retry?.allowed &&
    !waitingForStats &&
    (build.canRetryFailedJobs || build.canRetryFailedTriggeredJobs);

  // Rebuild and "New build" are independent of the current build's state — if
  // the user has permission to trigger builds they can do so regardless of
  // whether the current build is running, canceling, or finished. The backend
  // build creation endpoints have no running-state restriction.
  //
  // Note: `permissions.rebuild` is misleadingly named — it's the
  // `:trigger_build` permission, which gates BOTH "New build" and Rebuild
  // actions. The project-level `allowRebuilds` setting only restricts Rebuild;
  // a New build can still be started even when rebuilds are disabled.
  const projectAcceptsBuilds = !build.project.archived && !waitingForStats;
  const canTriggerBuilds = projectAcceptsBuilds && build.permissions.rebuild?.allowed;
  const canRebuild = canTriggerBuilds && build.project.allowRebuilds;

  const showDisabledRebuildOption =
    projectAcceptsBuilds && !canRebuild && build.permissions.rebuild?.reason !== "anonymous";

  const showDisabledForLoggedOut =
    projectAcceptsBuilds && build.permissions.rebuild?.reason === "anonymous";

  if (
    !showRetryFailures &&
    !canTriggerBuilds &&
    !showDisabledRebuildOption &&
    !showDisabledForLoggedOut
  ) {
    return null;
  }

  // Standalone "Retry Failures" — no rebuild / new-build affordances available.
  if (showRetryFailures && !canTriggerBuilds && !showDisabledRebuildOption) {
    return (
      <RetryFailedJobsButton
        build={build}
        setBuildData={setBuildData}
        className={classNames(buildActionButtonClassName, "shrink-0 whitespace-nowrap")}
        compactMode={true}
      />
    );
  }

  // Standalone disabled "Rebuild" — user can't trigger builds at all but the
  // project would allow it, so explain why with a disabled affordance.
  if (!showRetryFailures && !canTriggerBuilds && showDisabledRebuildOption) {
    return (
      <span
        className="btn btn-small btn-flex btn-default build-rebuild-button btn--disabled text-center"
        title={build.permissions.rebuild?.message}
      >
        <Icon
          icon="custom/outline/16x/arrows-refresh"
          style={{ width: 16, height: 16 }}
          className="mr-1"
        />
        <span>Rebuild</span>
      </span>
    );
  }

  // Logged-out prompt — disabled "New build" with a log-in tooltip.
  if (!showRetryFailures && !canTriggerBuilds && showDisabledForLoggedOut) {
    return (
      <button
        className="btn btn-small btn-flex btn-default build-rebuild-button btn--disabled text-center"
        title={build.permissions.rebuild?.message}
        disabled={true}
      >
        <Icon icon="heroicons/outline/plus" style={{ width: 16, height: 16 }} className="mr-1" />
        <span>New build</span>
      </button>
    );
  }

  // Standalone "New build" — user can start a new build but there's nothing
  // useful to put in a dropdown (no Retry Failures, no Rebuild items, no
  // disabled Rebuild hint).
  if (!showRetryFailures && canTriggerBuilds && !canRebuild && !showDisabledRebuildOption) {
    return (
      <a
        href={build.newPath}
        className={classNames(
          buildActionButtonClassName,
          "btn build-rebuild-button btn-flex shrink-0 whitespace-nowrap text-center hover:text-inherit focus:text-inherit",
        )}
        data-turbo={true}
        data-turbo-frame="modal_dialog"
      >
        <Icon icon="heroicons/outline/plus" style={{ width: 14, height: 14 }} className="mr-1" />
        <span>New build</span>
      </a>
    );
  }

  // Combo: default action + dropdown.
  // When there are failures to retry we make that the default option, otherwise
  // we default to "New build".
  const defaultAction = showRetryFailures ? (
    <RetryFailedJobsButton
      build={build}
      setBuildData={setBuildData}
      className={classNames(
        buildActionButtonClassName,
        "rounded-r-none hover:text-inherit focus:text-inherit",
      )}
      compactMode={true}
      disableTooltip={dropdownOpen}
    />
  ) : (
    <a
      href={build.newPath}
      className={classNames(
        buildActionButtonClassName,
        "btn build-rebuild-button btn-flex rounded-r-none text-center hover:text-inherit focus:text-inherit",
      )}
      data-turbo={true}
      data-turbo-frame="modal_dialog"
    >
      <Icon icon="heroicons/outline/plus" style={{ width: 14, height: 14 }} className="mr-1" />
      <span>New build</span>
    </a>
  );

  // "New build" appears in the dropdown only when it's not the default action
  // (i.e. when Retry Failures is the default). It depends only on the
  // trigger_build permission, NOT on `allowRebuilds`.
  const showNewBuildInDropdown = showRetryFailures && canTriggerBuilds;

  // The disabled-Rebuild tooltip explains why Rebuild is greyed out. If the
  // user has `:trigger_build` permission, no `permissions.rebuild.message` is
  // set — the only reason we can be in the disabled state is `allowRebuilds`
  // being false at the project level, so explain that explicitly.
  const disabledRebuildMessage = canTriggerBuilds
    ? "Rebuilds are disabled on this pipeline"
    : build.permissions.rebuild?.message;

  return (
    <div className="flex shrink-0">
      {defaultAction}
      <Dropdown width={200} className="inline-block" onToggle={setDropdownOpen}>
        <button
          className={classNames(
            buildActionButtonClassName,
            "btn btn-flex cursor-pointer select-none rounded-l-none border-l-0",
          )}
          title="Rebuild options"
        >
          <Icon icon="down-triangle" style={{ width: 7, height: 7 }} />
        </button>
        {canRebuild ? (
          <>
            <DropdownItem href={build.rebuildPath} data-method="post">
              <Icon icon="custom/outline/16x/arrows-refresh" style={{ width: 16, height: 16 }} />
              <span>Rebuild</span>
            </DropdownItem>
            <DropdownItem href={build.rebuildBranchPath} data-method="post">
              <Icon icon="custom/outline/git-branch" style={{ width: 16, height: 16 }} />
              <span>Rebuild this branch</span>
            </DropdownItem>
          </>
        ) : null}
        {showNewBuildInDropdown && (
          <>
            {canRebuild && <div className="my-1 h-px w-full bg-gray-500" />}
            <DropdownItem href={build.newPath} data-turbo={true} data-turbo-frame="modal_dialog">
              <Icon icon="heroicons/outline/plus" style={{ width: 14, height: 14 }} />
              <span>New build</span>
            </DropdownItem>
          </>
        )}
        {showDisabledRebuildOption ? (
          <DropdownItem disabled={true}>
            <span className="text-center" title={disabledRebuildMessage}>
              <Icon
                icon="custom/outline/16x/arrows-refresh"
                style={{ width: 16, height: 16 }}
                className="mr-1"
              />
              <span>Rebuild</span>
            </span>
          </DropdownItem>
        ) : null}
      </Dropdown>
    </div>
  );
}

function DropdownItem({
  children,
  disabled = false,
  ...props
}: React.HTMLProps<HTMLAnchorElement>) {
  const preventClick = (event) => {
    event.preventDefault();
  };
  return (
    <a
      {...props}
      onClick={disabled ? preventClick : undefined}
      rel="nofollow"
      className={classNames(
        "mx-1.5 flex items-center gap-1 rounded-md px-2 py-2 font-medium text-inherit hover:no-underline",
        {
          "hover:bg-purple-100 hover:text-purple-600": !disabled,
          "cursor-not-allowed opacity-50 hover:text-inherit": disabled,
        },
      )}
    >
      {children}
    </a>
  );
}
